import { useId, type ReactNode } from 'react';
import styles from './OptionCards.module.css';

export type OptionCard<Value extends string> = {
  readonly value: Value;
  readonly title: string;
  readonly description?: string | undefined;
  readonly icon?: ReactNode;
  readonly badge?: string | undefined;
  readonly disabled?: boolean | undefined;
};

export type OptionCardsProps<Value extends string> = {
  label: string;
  options: readonly OptionCard<Value>[];
  value: Value | undefined;
  onChange: (value: Value) => void;
  columns?: 1 | 2 | 3 | 4;
};

export const OptionCards = <Value extends string>({
  label,
  options,
  value,
  onChange,
  columns = 3,
}: OptionCardsProps<Value>) => {
  const name = useId();
  return (
    <fieldset className={styles['group']}>
      <legend className={styles['legend']}>{label}</legend>
      <div className={styles['grid']} data-columns={columns}>
        {options.map((option) => (
          <label key={option.value} className={styles['card']} data-disabled={option.disabled ?? false}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              disabled={option.disabled}
              className={styles['input']}
              onChange={() => {
                onChange(option.value);
              }}
            />
            {option.icon ? (
              <span className={styles['icon']} aria-hidden="true">
                {option.icon}
              </span>
            ) : null}
            <span className={styles['text']}>
              <span className={styles['title']}>
                {option.title}
                {option.badge ? <span className={styles['badge']}>{option.badge}</span> : null}
              </span>
              {option.description ? (
                <span className={styles['description']}>{option.description}</span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};
